import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { TextInput } from 'src/design/atoms/TextInput';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { PaperStack } from 'src/design/atoms/PaperStack';
import { useFormik } from 'formik';
import React from 'react';
import {
  DrawingDto,
  DrawingRequestDto,
  JobDto,
  ResourceAttachmentDto,
} from 'src/api/FiretrolApiClient';
import {
  NotificationCompositionRequest,
  UserDto,
} from 'src/api/FiretrolApiClient';
import {
  usePreviewDrawingPdf,
  useRotateDrawingExternalAccess,
  useSaveDrawing,
} from 'src/api/queries/drawings/useDrawingMutations';
import { useUploadTempAttachment } from 'src/api/queries/workflowModules/useAttachmentMutations';
import { FileDrop } from 'src/design/organisms/FileDrop/FileDrop';
import { applyAttachmentChange } from 'src/design/organisms/attachments/attachmentEdits';
import { WorkflowRecordSection } from 'src/app/workflows/WorkflowRecordSection';
import { workflowRecordFieldSchema } from 'src/app/workflows/workflowRecordFieldSchema';
import { useWorkflowPdfPreview } from 'src/app/workflows/useWorkflowPdfPreview';
import { useWorkflowAttachmentEdits } from 'src/app/workflows/useWorkflowAttachmentEdits';
import { useWorkflowState } from 'src/app/workflows/WorkflowButtons/useWorkflowState';
import { isStepEditable } from 'src/app/workflows/isStepEditable';
import { useRevokeExternalLinks } from 'src/app/workflows/useRevokeExternalLinks';
import { DrawingDocumentPicker } from 'src/routes/Jobs/Drawings/components/DrawingDocumentPicker';
import { useNotificationComposer } from 'src/hooks/useNotificationComposer';
import { WorkflowFormFooter } from 'src/app/workflows/WorkflowFormFooter';
import { useTransitionComposer } from 'src/app/workflows/useTransitionComposer';
import { useWorkflowFormTransitions } from 'src/app/workflows/useWorkflowFormTransitions';
import { isTerminalStatus } from 'src/app/workflows/workflowLifecycle';
import { useAuth } from 'src/hooks/useAuth';
import { useWorkflowSubmission } from 'src/hooks/useWorkflowSubmission';
import { FIELD_LIMITS, getErrorMessage, logger } from 'src/utils';
import * as Yup from 'yup';
import { IContact } from 'src/api/models/Organization';
import { contactDtoToIContact } from 'src/app/workflows/contactDtoToIContact';
import { useWorkflowFormValidation } from 'src/app/workflows/useWorkflowFormValidation';
import { useResolveAttachmentUrl } from 'src/api/queries/attachments/useAttachmentMutations';

interface FormTarget {
  name: string;
  value: string;
}

const DRAWING_FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  assigneeUserId: 'Assignee',
  company: 'Company Name',
  intentOfReview: 'Intent of Review',
  milestonePhaseItemId: 'Linked Milestone Item',
  externalDrawingNumber: 'Drawing # (External)',
};

const validationSchema = Yup.object().shape({
  ...workflowRecordFieldSchema,
  externalDrawingNumber: Yup.string()
    .nullable()
    .max(
      FIELD_LIMITS.singleLine,
      `Drawing # (External) must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    ),
});

/** Which slice of the form to render; omit to render the full single-column form (create flow). */
export type DrawingFormSection =
  | 'details'
  | 'attachments'
  | 'notifications'
  | 'history';

export interface DrawingFormValues extends DrawingDto {
  filesToAdd: ResourceAttachmentDto[];
  filesToRemove: ResourceAttachmentDto[];
}

interface IDrawingFormProps {
  initialValues: DrawingDto;
  selectedJob: JobDto;
  onSaved?: (drawing: DrawingDto) => void;
  onCreated?: (drawing: DrawingDto) => void;
  /** In ViewDrawing the form is split across tabs; this selects the visible slice. Omit for the single-column create flow. */
  activeSection?: DrawingFormSection;
  jobUsers?: UserDto[];
  /** Content for non-form tabs (notifications, history) rendered above the persistent footer. */
  children?: React.ReactNode;
}

export const DrawingForm: React.FC<IDrawingFormProps> = ({
  onSaved,
  onCreated,
  initialValues,
  selectedJob,
  activeSection,
  jobUsers = [],
  children,
}) => {
  const { user, isAdmin } = useAuth();

  const formik = useFormik<DrawingFormValues>({
    initialValues: {
      ...initialValues,
      filesToAdd: [],
      filesToRemove: [],
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async () => {},
  });
  const { values, setFieldValue } = formik;

  const { currentStep } = useWorkflowState(
    values.workflow ?? null,
    values.stepIndex ?? -1,
  );

  const [submittedToContact, setSubmittedToContact] =
    React.useState<IContact | null>(
      contactDtoToIContact(
        initialValues.submittedToContact,
        initialValues.submittedToContactId,
      ),
    );

  const composer = useNotificationComposer(
    'drawing',
    selectedJob.id,
    values.id ?? '',
  );
  const transition = useTransitionComposer(composer.loadPreview);

  const saveDrawing = useSaveDrawing();
  const previewDrawingPdf = usePreviewDrawingPdf();
  const rotateExternalAccess = useRotateDrawingExternalAccess();
  const uploadTempAttachment = useUploadTempAttachment();
  const downloadAttachment = useResolveAttachmentUrl();

  const pdfPreview = useWorkflowPdfPreview({
    fetchPdf: () =>
      previewDrawingPdf.mutateAsync({
        jobId: selectedJob.id,
        id: values.id ?? '',
      }),
    logLabel: 'Failed to load drawing PDF preview',
  });

  const revoker = useRevokeExternalLinks<DrawingDto>({
    rotate: () =>
      rotateExternalAccess.mutateAsync({
        jobId: selectedJob.id,
        id: values.id ?? '',
      }),
    onRevoked: (dto) => {
      formik.resetForm({
        values: {
          ...values,
          attachments: dto.attachments ?? values.attachments,
        },
      });
      onSaved?.(dto);
    },
    logLabel: 'Failed to revoke drawing external links',
  });

  const buildRequest = (
    overrides: Partial<DrawingRequestDto> = {},
  ): DrawingRequestDto => ({
    id: values.id || undefined,
    title: values.title,
    type: values.type,
    intentOfReview: values.intentOfReview,
    externalDrawingNumber: values.externalDrawingNumber,
    company: values.company,
    status: values.status,
    stepIndex: values.stepIndex,
    workflowId: values.workflowId,
    assigneeUserId: values.assigneeUserId,
    reporterUserId: values.reporterUserId,
    milestonePhaseItemId: values.milestonePhaseItemId,
    submittedToContactId: values.submittedToContactId,
    filesToAdd: values.filesToAdd?.map((f) => ({ id: f.id })),
    filesToRemove: values.filesToRemove?.map((f) => ({ id: f.id })),
    saveOnly: false,
    notification: undefined,
    ...overrides,
  });

  const {
    submitWorkflow,
    voidWorkflow,
    saveOnly,
    error,
    fieldErrors,
    isSubmitting,
  } = useWorkflowSubmission<DrawingFormValues, DrawingRequestDto>(
    values,
    selectedJob.id,
    {
      mutations: { save: saveDrawing },
      toRequest: (_vals, overrides) =>
        buildRequest({
          status: overrides?.status,
          stepIndex: overrides?.stepIndex,
          saveOnly: overrides?.saveOnly ?? false,
          notification: overrides?.notification,
        }),
      entityName: 'Drawing',
      toast: { getEntityNumber: (d) => d.drawingNumber ?? '' },
    },
    {
      onCreated,
      onSaved,
      setValues: (dto) => {
        formik.resetForm({
          values: {
            ...values,
            ...dto,
            attachments: dto.attachments ?? values.attachments,
            filesToAdd: [],
            filesToRemove: [],
            workflow: dto.workflow ?? values.workflow,
          },
        });
      },
    },
  );

  const setValue = ({ target }: { target: FormTarget }) => {
    setFieldValue(target.name, target.value);
  };

  const { passesValidation, fieldError } =
    useWorkflowFormValidation<DrawingFormValues>(
      formik,
      fieldErrors,
      DRAWING_FIELD_LABELS,
    );

  const { onSubmitClick, submitTransition } =
    useWorkflowFormTransitions<DrawingFormValues>({
      values,
      submitWorkflow,
      transition,
      entityLabel: 'drawing',
    });

  const handleSubmitClick = async (status: string, nextStepIndex: number) => {
    if (!(await passesValidation())) return;
    await onSubmitClick(status, nextStepIndex);
  };

  const handleSaveOnly = async () => {
    if (!(await passesValidation())) return;
    await saveOnly();
  };

  const { saveComment, addDocument } = useWorkflowAttachmentEdits(
    formik.setValues,
    selectedJob.id,
  );

  const isDisabled = !isStepEditable({
    isAdmin,
    currentStep,
    status: values.status,
  });
  const reporter = values.reporter
    ? values.reporter.fullName
    : `${user?.firstName ?? ''} ${user?.lastName ?? ''}`;
  const customerNumber = selectedJob.customerNumber?.trim() ?? '';
  const isTerminal = isTerminalStatus(values.status);

  const showDetails = !activeSection || activeSection === 'details';
  const showAttachments = !activeSection || activeSection === 'attachments';

  const attachmentsSection = (
    <PaperStack>
      <Group justify="space-between">
        <Title order={4}>Document</Title>
        {values.id && (values.attachments ?? []).some((f) => f.externalUrl) && (
          <SecondaryButton onClick={revoker.openModal}>
            Revoke links
          </SecondaryButton>
        )}
      </Group>
      <DrawingDocumentPicker jobId={selectedJob.id} onAttached={addDocument} />
      <FileDrop
        disabled={currentStep?.isLastStep || values.status === 'Voided'}
        maxFiles={1}
        revisions={[...(values.revisions ?? []), ...values.filesToRemove]}
        files={values.attachments ?? []}
        onChange={(files, change) => {
          const edits = applyAttachmentChange(
            { added: values.filesToAdd, removed: values.filesToRemove },
            change,
          );
          formik.setValues((v) => ({
            ...v,
            attachments: files as ResourceAttachmentDto[],
            filesToAdd: edits.added as ResourceAttachmentDto[],
            filesToRemove: edits.removed as ResourceAttachmentDto[],
          }));
        }}
        onCommentSave={saveComment}
        onCopyLink={revoker.copyLink}
        uploadFile={(file) =>
          uploadTempAttachment.mutateAsync({ typeId: 'drawings', file })
        }
        downloadFile={(id) => downloadAttachment.mutateAsync(id)}
      />
    </PaperStack>
  );

  return (
    <Stack gap="md" pb="md">
      {showDetails && (
        <>
          <PaperStack>
            <Group justify="space-between">
              <Title order={4}>Details</Title>
              {values.id && (
                <SecondaryButton onClick={pdfPreview.open}>
                  Preview PDF
                </SecondaryButton>
              )}
            </Group>
            <SimpleGrid cols={{ base: 1, md: 2 }}>
              <Stack gap="sm">
                <TextInput label="Job" value={selectedJob.name} disabled />
                <TextInput
                  label="Client"
                  value={selectedJob.customerName}
                  disabled
                />
                <TextInput label="Reporter" value={reporter} disabled />
              </Stack>
              <Stack gap="sm">
                <TextInput
                  label="Number"
                  name="drawingNumber"
                  value={values.drawingNumber ?? ''}
                  disabled
                  placeholder="Auto Generated"
                />
                <TextInput
                  label="Drawing # (External)"
                  name="externalDrawingNumber"
                  disabled={isDisabled}
                  value={
                    isDisabled && !values.externalDrawingNumber
                      ? 'N/A'
                      : values.externalDrawingNumber ?? ''
                  }
                  onChange={setValue}
                  onBlur={formik.handleBlur}
                  maxLength={FIELD_LIMITS.singleLine}
                  error={fieldError('externalDrawingNumber')}
                  placeholder="ex: DRAWING-XXXX"
                />
                <TextInput
                  label="Status"
                  name="status"
                  value={values.status ?? ''}
                  disabled
                  placeholder="N/A"
                />
              </Stack>
            </SimpleGrid>
            {isAdmin && (
              <TextInput
                label="Assigned Workflow"
                description="(Admin Only)"
                name="workflow"
                value={values.workflow?.name}
                disabled
              />
            )}
          </PaperStack>
          <WorkflowRecordSection
            sectionTitle="Drawing"
            title={values.title}
            assigneeUserId={values.assigneeUserId}
            company={values.company}
            intentOfReview={values.intentOfReview}
            milestonePhaseItemId={values.milestonePhaseItemId}
            disabled={isDisabled}
            jobId={selectedJob.id}
            customerNumber={customerNumber}
            customerName={selectedJob.customerName ?? ''}
            submittedToContact={submittedToContact}
            onFieldChange={setFieldValue}
            onSubmittedToChange={setSubmittedToContact}
            fieldError={fieldError}
          />
        </>
      )}

      {showAttachments && (
        <>
          {showDetails && <Divider />}
          {attachmentsSection}
        </>
      )}

      {children}

      <WorkflowFormFooter
        dirty={formik.dirty}
        showButtons={!isTerminal}
        error={error}
        onSubmitClick={handleSubmitClick}
        workflow={values.workflow ?? null}
        onSaveOnlyClick={handleSaveOnly}
        onVoidClick={voidWorkflow}
        stepIndex={values.stepIndex ?? -1}
        composerOpen={transition.opened}
        onComposerClose={transition.cancel}
        preview={composer.preview}
        jobUsers={jobUsers}
        customerNumber={customerNumber}
        composerSubmitting={isSubmitting}
        composerTitle={`${
          transition.pending?.stepLabel ?? 'Drawing'
        } — notification`}
        attachPdfLabel="Attach Drawing PDF"
        onSend={submitTransition}
        onSkip={submitTransition}
        onReloadPreview={composer.reloadPreview}
        previewOpen={pdfPreview.opened}
        onPreviewClose={pdfPreview.close}
        pdfTitle="Drawing PDF"
        previewUrl={pdfPreview.url}
        previewLoading={pdfPreview.loading}
        revokeOpen={revoker.modalOpen}
        onRevokeClose={revoker.closeModal}
        onRevokeConfirm={revoker.revoke}
        revoking={revoker.revoking}
        revokeEntityNoun="Drawing"
        revokeItemsNoun="attachments"
      />
    </Stack>
  );
};
